import * as Sentry from "@sentry/react";
import { ErrorBoundary, ErrorBoundaryFallbackProps } from "@suspensive/react";
import { QueryErrorResetBoundary } from "@tanstack/react-query";
import LogRocket from "logrocket";
import React, { Suspense } from "react";
import { useLocation } from "react-router-dom";
import { GenericErrorPageFallback } from "shared/components/empty/GenericErrorPage";
import { GenericErrorViewFallback } from "shared/components/empty/GenericErrorView";
import { LoadingState } from "shared/components/empty/LoadingState";
import { AppConfig } from "shared/config/AppConfig";
import { isDev } from "shared/utils/DevUtils";

export interface QueryBoundaryProps {
  isPage?: boolean;
  ErrorComponent?: React.FC<ErrorBoundaryFallbackProps>;
  pendingFallback?: React.ReactNode;
}

export const QueryBoundary: React.FC<
  React.PropsWithChildren<QueryBoundaryProps>
> = ({
  isPage = false,
  pendingFallback = <LoadingState />,
  ErrorComponent = isPage ? GenericErrorPageFallback : GenericErrorViewFallback,
  children,
}) => {
  const { pathname } = useLocation();
  return (
    <QueryErrorResetBoundary>
      <ErrorBoundary
        fallback={ErrorComponent}
        resetKeys={[pathname]}
        onError={(error) => {
          if (!isDev) {
            Sentry.captureException(error);
            if (AppConfig.logRocket) {
              LogRocket.track("query-error", { error: error.message });
            }
          }
        }}
      >
        <Suspense fallback={pendingFallback}>{children}</Suspense>
      </ErrorBoundary>
    </QueryErrorResetBoundary>
  );
};
